/**
 * Step 12 — pickups: mote drops, magnet, collection, denomination merge, health,
 * power-ups, and the Lumensurge meter economy.
 *
 * GDD: §9.2 (denominations, drop table), §9.5 (Lumensurge), §9.6 (power-ups),
 * §9.7 (health), §9.8 (anti-degenerate caps and the merge rule), §10.9 (placement,
 * magnet, despawn).
 *
 * Determinism: every roll consumes `w.rng.loot` and nothing else. Iteration is over
 * `pool.liveIndices()` ascending. Nothing here allocates per tick.
 */

import { ENEMIES, POWERUP_POOL, bossById, enemyById } from '../core/content';
import type { Enemy, Pickup } from './entities';
import type { World } from './world';
import { pickupRadius } from './build';
import { addSurgeMeter } from './player';
import { applyDamageToEnemy } from './damage';
import { budgetForMinute } from './director';
import { xpSupplyForMinute } from './progression';
import { wrapDeltaX, wrapDeltaY, wrapDist2, wrapPos } from './wrap';

// --- §9.2.1 denominations (spine §12: rings 0/1/2 double-code 1/5/25) ---

export const MOTE_SHARD_VALUE = 1;
export const MOTE_CLUSTER_VALUE = 5;
export const MOTE_BLOOM_VALUE = 25;

// --- §9.8 caps ---

export const MOTE_ALIVE_CAP = 400;
export const HEALTH_ALIVE_CAP = 3;
export const POWERUP_ALIVE_CAP = 2;

// --- §10.9 placement / magnet / despawn ---

export const DROP_JITTER_U = 0.75;
export const BOSS_DROP_RING_U = 4;
export const MAGNET_ACCEL = 60;
export const MAGNET_MAX_SPEED = 24;
export const COLLECT_RADIUS = 0.8;
export const TRAWL_PULL_SPEED = 24;
export const MOTE_DESPAWN_S = 60;
export const HEALTH_DESPAWN_S = 45;
export const POWERUP_DESPAWN_S = 30;
export const BLINK_WARNING_S = 3;

// --- §9.7 health ---

export const HEAL_AMOUNT = 25;

// --- §9.6 power-ups ---

export const POWERUP_LUMEN_TRAWL = 'pickup.lumen-trawl';
export const POWERUP_LUCIFERIN_OVERDRIVE = 'pickup.luciferin-overdrive';
export const POWERUP_PRISM_WARD = 'pickup.prism-ward';
export const POWERUP_CASCADE_FLARE = 'pickup.cascade-flare';
export const POWERUP_SURGE_ANCHOR = 'pickup.surge-anchor';
export const POWERUP_ABYSSAL_STILL = 'pickup.abyssal-still';

export const TRAWL_DURATION_S = 6;
export const OVERDRIVE_DURATION_S = 8;
export const OVERDRIVE_HASTE = 1.0;
export const PRISM_WARD_DURATION_S = 12;
export const SURGE_ANCHOR_DURATION_S = 10;
export const ABYSSAL_STILL_DURATION_S = 6;
export const ABYSSAL_STILL_ENEMY_SPEED = 0.6;
export const ABYSSAL_STILL_PROJECTILE_SPEED = 0.7;
export const ABYSSAL_STILL_BOSS_SPEED = 0.85;
export const CASCADE_FLARE_DAMAGE = 150;
export const CASCADE_FLARE_RADIUS = 16;

const STEP = 1 / 60;

/** Fixed draw order for the §9.6 weighted pool. Order is part of replay identity. */
const POWERUP_IDS: readonly string[] = [
  POWERUP_LUMEN_TRAWL,
  POWERUP_LUCIFERIN_OVERDRIVE,
  POWERUP_PRISM_WARD,
  POWERUP_CASCADE_FLARE,
  POWERUP_SURGE_ANCHOR,
  POWERUP_ABYSSAL_STILL,
];

const POWERUP_WEIGHTS = new Float64Array(POWERUP_IDS.length);
const POWERUP_DURATIONS = new Float64Array(POWERUP_IDS.length);

(() => {
  const raw = (POWERUP_POOL as { weights?: Record<string, number> }).weights;
  for (let i = 0; i < POWERUP_IDS.length; i++) {
    const wgt = raw === undefined ? 0 : raw[POWERUP_IDS[i]];
    POWERUP_WEIGHTS[i] = typeof wgt === 'number' ? wgt : 0;
  }
  POWERUP_DURATIONS[0] = TRAWL_DURATION_S;
  POWERUP_DURATIONS[1] = OVERDRIVE_DURATION_S;
  POWERUP_DURATIONS[2] = PRISM_WARD_DURATION_S;
  POWERUP_DURATIONS[3] = 0; // cascade flare is instant on pickup
  POWERUP_DURATIONS[4] = SURGE_ANCHOR_DURATION_S;
  POWERUP_DURATIONS[5] = ABYSSAL_STILL_DURATION_S;
})();

/** Sum of the §9.6 weights. Acceptance: exactly 1.0 at data load. */
export function powerupWeightSum(): number {
  let s = 0;
  for (let i = 0; i < POWERUP_WEIGHTS.length; i++) s += POWERUP_WEIGHTS[i];
  return s;
}

// --- per-world state ---

interface PickupState {
  /** Expiry tick per POWERUP_IDS index; <= w.tick means inactive. */
  until: Int32Array;
  /** Prism Ward charge (§9.6: exactly one absorbed instance). */
  wardCharges: number;
  /** §9.6 Surge Anchor — the meter level the window holds; -1 when not anchored. */
  anchorMeter: number;
  moteCount: number;
  healthCount: number;
  powerupCount: number;
  /** AMD-45 — fractional XP banked below one whole mote by `scaledXp`. */
  xpCarry: number;
  installed: boolean;
}

const STATES = new WeakMap<World, PickupState>();

function stateOf(w: World): PickupState {
  let st = STATES.get(w);
  if (st === undefined) {
    st = {
      until: new Int32Array(POWERUP_IDS.length),
      wardCharges: 0,
      anchorMeter: -1,
      moteCount: 0,
      healthCount: 0,
      powerupCount: 0,
      xpCarry: 0,
      installed: false,
    };
    STATES.set(w, st);
  }
  return st;
}

/** Clears power-up timers and cached counts. Call on run reset. */
export function resetPickups(w: World): void {
  const st = stateOf(w);
  st.until.fill(0);
  st.wardCharges = 0;
  st.anchorMeter = -1;
  st.moteCount = 0;
  st.healthCount = 0;
  st.powerupCount = 0;
  st.xpCarry = 0;
}

/**
 * Subscribes drop resolution to `death` (§9.2.2). Idempotent, and the only automatic
 * drop path — a caller that invokes `dropsForKill` itself must pass `autoDrops = false`.
 */
export function installPickups(w: World, autoDrops = true): void {
  const st = stateOf(w);
  if (st.installed) return;
  st.installed = true;
  if (!autoDrops) return;
  w.bus.on('death', (payload) => {
    dropsAt(w, payload.def_id, payload.elite, payload.pos.x, payload.pos.y);
  });
}

// --- §9.6 power-up readers (presentation + other sim systems) ---

function powerupIndex(id: string): number {
  for (let i = 0; i < POWERUP_IDS.length; i++) {
    if (POWERUP_IDS[i] === id) return i;
  }
  return -1;
}

export function powerupActive(w: World, id: string): boolean {
  const i = powerupIndex(id);
  if (i < 0) return false;
  return stateOf(w).until[i] > w.tick;
}

/** Remaining seconds of a power-up, 0 when inactive. */
export function powerupRemaining(w: World, id: string): number {
  const i = powerupIndex(id);
  if (i < 0) return 0;
  const left = stateOf(w).until[i] - w.tick;
  return left > 0 ? left * STEP : 0;
}

/** §9.6 Lumen Trawl — arena-wide homing for XP motes only. */
export function trawlActive(w: World): boolean {
  return stateOf(w).until[0] > w.tick;
}

/** §9.6 Luciferin Overdrive — additive haste contribution (§4.6 ADD bucket). */
export function powerupHaste(w: World): number {
  return stateOf(w).until[1] > w.tick ? OVERDRIVE_HASTE : 0;
}

/**
 * §9.6 Prism Ward — consumes the charge and returns true when the incoming damage
 * instance must be fully negated (0 HP loss, no surge loss, no knockback).
 */
export function consumePrismWard(w: World): boolean {
  const st = stateOf(w);
  if (st.until[2] > w.tick && st.wardCharges > 0) {
    st.wardCharges = 0;
    st.until[2] = 0;
    return true;
  }
  return false;
}

/** §9.6 Surge Anchor — meter frozen: no decay, no hit loss, no leech drain. */
export function surgeFrozen(w: World): boolean {
  return stateOf(w).until[4] > w.tick;
}

/** §9.6 Abyssal Still — trash enemy speed multiplier. */
export function enemySpeedMult(w: World): number {
  return stateOf(w).until[5] > w.tick ? ABYSSAL_STILL_ENEMY_SPEED : 1;
}

/** §9.6 Abyssal Still — boss speed multiplier (never the trash value). */
export function bossSpeedMult(w: World): number {
  return stateOf(w).until[5] > w.tick ? ABYSSAL_STILL_BOSS_SPEED : 1;
}

/** §9.6 Abyssal Still — enemy projectile speed multiplier. */
export function enemyProjectileSpeedMult(w: World): number {
  return stateOf(w).until[5] > w.tick ? ABYSSAL_STILL_PROJECTILE_SPEED : 1;
}

/**
 * §9.6 Surge Anchor — the meter is frozen: no decay, no hit loss, no leech drain, but
 * gains still apply. R1 owns the meter (`player.addSurgeMeter` / `dropSurgeTiers`) and
 * does not consult the anchor, so the window is enforced here by holding the high-water
 * value and restoring it at step 12 of the same tick any loss occurred.
 */
function holdSurgeAnchor(w: World): void {
  const st = stateOf(w);
  if (st.until[4] <= w.tick) {
    st.anchorMeter = -1;
    return;
  }
  const meter = w.player.surge_progress;
  if (st.anchorMeter < 0 || meter > st.anchorMeter) {
    st.anchorMeter = meter;
  } else if (meter < st.anchorMeter) {
    addSurgeMeter(w, st.anchorMeter - meter);
  }
}

// --- AMD-45: the XP supply normalizer ---

/**
 * `drops.xp_motes / threat_score`, averaged over the archetype table at load.
 *
 * §9.2.2 ratifies every archetype's XP against its §8.3 threat cost, so this ratio is the
 * conversion the §8.2 budget curve used to perform: XP supply per minute *was*
 * `budget_per_min × XP_PER_THREAT`. Deriving it from content rather than writing 0.25 here
 * keeps the property that matters after AMD-45 — the JSON sets each archetype's **relative**
 * worth, and `xpSupplyForMinute` alone sets the absolute level.
 */
const XP_PER_THREAT = (() => {
  let sum = 0;
  let n = 0;
  for (const raw of ENEMIES as readonly { threat_score: number; drops?: Record<string, unknown> }[]) {
    if (raw.drops === undefined || raw.threat_score <= 0) continue;
    const flat = raw.drops.xp_motes;
    const avg = raw.drops.xp_motes_avg;
    const xp = typeof flat === 'number' ? flat : typeof avg === 'number' ? avg : -1;
    if (xp < 0) continue;
    sum += xp / raw.threat_score;
    n++;
  }
  return n > 0 ? sum / n : 0.25;
})();

/** Exposed for the AMD-45 acceptance test; not read by the sim. */
export function xpPerThreat(): number {
  return XP_PER_THREAT;
}

/**
 * How much a §9.2.2 mote value is worth this minute.
 *
 * `xpSupplyForMinute` says what the minute is allowed to pay. The minute's realized threat
 * budget — Descent tier and every measurement scalar included — says how many bodies that
 * payment has to be spread across. Dividing one by the other is what severs XP from
 * difficulty: raise the §8.2 curve and each body is worth proportionally less, so the
 * minute pays out the same XP against strictly more pressure.
 */
function xpSupplyScale(w: World): number {
  const minute = Math.floor(w.tick / 3600);
  const baseline = budgetForMinute(w, minute) * XP_PER_THREAT;
  if (baseline <= 1e-9) return 1;
  return xpSupplyForMinute(minute) / baseline;
}

/**
 * Applies {@link xpSupplyScale} and banks the fraction below a whole mote, so a run's
 * realized supply tracks the schedule instead of losing a floor() on every kill. The carry
 * is per-world f64 state and consumes no RNG — the same shape as §9.13's `ichorCenti`.
 */
function scaledXp(w: World, xp: number): number {
  if (xp <= 0) return 0;
  const st = stateOf(w);
  st.xpCarry += xp * xpSupplyScale(w);
  const whole = Math.floor(st.xpCarry);
  st.xpCarry -= whole;
  return whole;
}

// --- drops (§9.2, §9.7, §9.6) ---

/** §9.2.1 ring count for a merged/decomposed value. */
function ringsForValue(v: number): number {
  if (v >= MOTE_BLOOM_VALUE) return 2;
  if (v >= MOTE_CLUSTER_VALUE) return 1;
  return 0;
}

function acquirePickup(w: World, kind: Pickup['pickup_kind']): Pickup | null {
  const p = w.pickups.acquire();
  if (p === null) {
    w.bus.emit('pool_exhausted', { pool: 'pickups', denied_kind: kind });
    return null;
  }
  p.id = w.allocId();
  p.pickup_kind = kind;
  p.faction = 'neutral';
  p.targetable = false;
  p.spawn_tick = w.tick;
  p.vel.x = 0;
  p.vel.y = 0;
  p.magnet = false;
  p.rings = 0;
  p.powerup_id = '';
  p.bob_phase = 0;
  p.hp = 1;
  p.max_hp = 1;
  p.status_count = 0;
  p.hitbox_radius = kind === 'xp_mote' ? 0.35 : 0.5;
  return p;
}

/** §10.9 — jitter ≤ 0.75 u on the `loot` stream, canonicalised onto the torus (AMD-24). */
function placeJittered(w: World, p: Pickup, x: number, y: number): void {
  const a = w.rng.loot.next() * Math.PI * 2;
  const d = Math.sqrt(w.rng.loot.next()) * DROP_JITTER_U;
  p.pos.x = x + Math.cos(a) * d;
  p.pos.y = y + Math.sin(a) * d;
  w.wrapToArena(p.pos, 1);
}

/**
 * §9.8.2 — at the 400-mote cap, fold the two oldest motes into one pickup carrying
 * their summed value, re-coded to the highest denomination that fits. Σ value is
 * invariant, so the 512-slot pool never exhausts (§14.3's assertion never fires).
 */
export function mergeOldestMotePair(w: World): boolean {
  const list = w.pickups.liveIndices();
  let a = -1;
  let b = -1;
  for (let i = 0; i < list.length; i++) {
    const p = w.pickups.slots[list[i]];
    if (p.pickup_kind !== 'xp_mote') continue;
    if (a < 0 || p.id < w.pickups.slots[a].id) {
      b = a;
      a = list[i];
    } else if (b < 0 || p.id < w.pickups.slots[b].id) {
      b = list[i];
    }
  }
  if (a < 0 || b < 0) return false;
  const keep = w.pickups.slots[a];
  const drop = w.pickups.slots[b];
  keep.value += drop.value;
  keep.rings = ringsForValue(keep.value);
  keep.hitbox_radius = 0.35;
  w.pickups.release(drop);
  const st = stateOf(w);
  st.moteCount--;
  return true;
}

function spawnMoteOfValue(w: World, value: number, x: number, y: number): void {
  const st = stateOf(w);
  if (st.moteCount >= MOTE_ALIVE_CAP && !mergeOldestMotePair(w)) return;
  const p = acquirePickup(w, 'xp_mote');
  if (p === null) return;
  p.value = value;
  p.rings = ringsForValue(value);
  placeJittered(w, p, x, y);
  st.moteCount++;
}

/**
 * §9.2.1 — greedy denomination decomposition (25 / 5 / 1). Deterministic, consumes
 * no RNG beyond the per-pickup placement jitter.
 */
export function spawnMotes(w: World, value: number, x: number, y: number): void {
  let v = Math.floor(value);
  if (v <= 0) return;
  while (v >= MOTE_BLOOM_VALUE) {
    spawnMoteOfValue(w, MOTE_BLOOM_VALUE, x, y);
    v -= MOTE_BLOOM_VALUE;
  }
  while (v >= MOTE_CLUSTER_VALUE) {
    spawnMoteOfValue(w, MOTE_CLUSTER_VALUE, x, y);
    v -= MOTE_CLUSTER_VALUE;
  }
  while (v >= MOTE_SHARD_VALUE) {
    spawnMoteOfValue(w, MOTE_SHARD_VALUE, x, y);
    v -= MOTE_SHARD_VALUE;
  }
}

/** §9.7 / §9.8.3 — health drop; overflow beyond the alive cap converts to a bloom. */
export function spawnHealth(w: World, x: number, y: number): void {
  const st = stateOf(w);
  if (st.healthCount >= HEALTH_ALIVE_CAP) {
    spawnMoteOfValue(w, MOTE_BLOOM_VALUE, x, y);
    return;
  }
  const p = acquirePickup(w, 'health');
  if (p === null) return;
  p.value = HEAL_AMOUNT;
  placeJittered(w, p, x, y);
  st.healthCount++;
}

/** §9.6 / §9.8.3 — power-up drop; overflow beyond the alive cap converts to a bloom. */
export function spawnPowerup(w: World, powerupId: string, x: number, y: number): void {
  const st = stateOf(w);
  if (st.powerupCount >= POWERUP_ALIVE_CAP) {
    spawnMoteOfValue(w, MOTE_BLOOM_VALUE, x, y);
    return;
  }
  const p = acquirePickup(w, 'powerup');
  if (p === null) return;
  p.value = 0;
  p.powerup_id = powerupId;
  placeJittered(w, p, x, y);
  st.powerupCount++;
}

/** §9.6 — one weighted draw on the `loot` stream. */
export function drawPowerupId(w: World): string {
  const r = w.rng.loot.next() * powerupWeightSum();
  let acc = 0;
  for (let i = 0; i < POWERUP_IDS.length; i++) {
    acc += POWERUP_WEIGHTS[i];
    if (r < acc) return POWERUP_IDS[i];
  }
  return POWERUP_IDS[POWERUP_IDS.length - 1];
}

/**
 * §9.2.2 / §9.6 / §9.7 — resolve every drop for one kill.
 * `loot` consumption order is fixed: xp value → health roll → power-up roll →
 * power-up pool draw → placement jitter per spawned pickup.
 */
export function dropsForKill(w: World, e: Enemy): void {
  dropsAt(w, e.def_id, e.elite, e.pos.x, e.pos.y);
}

/** Drop resolution keyed by content ID — the form the `death` event carries. */
export function dropsAt(w: World, defId: string, elite: boolean, x: number, y: number): void {
  const boss = bossById.get(defId);
  if (boss !== undefined) {
    const drops = boss.drops as { xp_motes?: number; health_pickups?: number; powerups?: number } | undefined;
    if (drops === undefined) return;
    const xp = drops.xp_motes === undefined ? 0 : drops.xp_motes;
    const heals = drops.health_pickups === undefined ? 0 : drops.health_pickups;
    const ups = drops.powerups === undefined ? 0 : drops.powerups;
    // §10.9 — boss drops sit on a ring r = 4 u around the death point, evenly spaced.
    const spokes = heals + ups > 0 ? heals + ups : 1;
    spawnMotes(w, xp, x, y);
    for (let i = 0; i < heals; i++) {
      const a = (Math.PI * 2 * i) / spokes;
      spawnHealth(w, x + Math.cos(a) * BOSS_DROP_RING_U, y + Math.sin(a) * BOSS_DROP_RING_U);
    }
    for (let i = 0; i < ups; i++) {
      const id = drawPowerupId(w);
      const a = (Math.PI * 2 * (heals + i)) / spokes;
      spawnPowerup(w, id, x + Math.cos(a) * BOSS_DROP_RING_U, y + Math.sin(a) * BOSS_DROP_RING_U);
    }
    return;
  }

  const def = enemyById.get(defId);
  if (def === undefined) return;
  const drops = def.drops as unknown as {
    xp_motes?: number;
    xp_motes_avg?: number;
    health_chance?: number;
    powerup_chance?: number;
  };

  let xp = 0;
  if (drops.xp_motes !== undefined) {
    xp = drops.xp_motes;
  } else if (drops.xp_motes_avg !== undefined) {
    // §9.2.2 glimmerfry: 0.4 avg → 40% chance of exactly 1 (`loot` stream).
    xp = w.rng.loot.next() < drops.xp_motes_avg ? 1 : 0;
  }
  // §9.2.2 — elites multiply XP value only; drop chances are NOT multiplied.
  if (elite) xp *= 5;
  // AMD-45 — an elite is 4x the threat cost, so it is also 4x of the minute's supply
  // spread across one body. The scale divides that back out with everything else.
  xp = scaledXp(w, xp);

  const healthChance = drops.health_chance === undefined ? 0 : drops.health_chance;
  const powerupChance = drops.powerup_chance === undefined ? 0 : drops.powerup_chance;
  const rollHealth = w.rng.loot.next() < healthChance;
  const rollPowerup = w.rng.loot.next() < powerupChance;
  const powerId = rollPowerup ? drawPowerupId(w) : '';

  if (xp > 0) spawnMotes(w, xp, x, y);
  if (rollHealth) spawnHealth(w, x, y);
  if (rollPowerup) spawnPowerup(w, powerId, x, y);
}

// --- collection ---

function activatePowerup(w: World, id: string, x: number, y: number): void {
  const st = stateOf(w);
  const i = powerupIndex(id);
  if (i < 0) return;
  if (id === POWERUP_CASCADE_FLARE) {
    cascadeFlare(w, x, y);
    return;
  }
  // §9.6 — one active instance each; re-pickup refreshes, never stacks.
  st.until[i] = w.tick + Math.round(POWERUP_DURATIONS[i] * 60);
  if (id === POWERUP_PRISM_WARD) st.wardCharges = 1;
}

/**
 * §9.6 Cascade Flare — instant 150 damage to every enemy whose centre is inside the
 * 16 u disc (bosses included). Routed through the §4.1 pipeline so kills credit drops
 * normally; the strike cannot crit (no stocked-bomb variance, spine §1 boundary).
 */
export function cascadeFlare(w: World, x: number, y: number): void {
  const list = w.enemies.liveIndices();
  const r2 = CASCADE_FLARE_RADIUS * CASCADE_FLARE_RADIUS;
  for (let i = 0; i < list.length; i++) {
    const e = w.enemies.slots[list[i]];
    if (!e.alive || e.death_anim > 0) continue;
    if (wrapDist2(x, y, e.pos.x, e.pos.y) > r2) continue;
    // A flare goes off around the player, not against a body: no contact point.
    applyDamageToEnemy(w, e, CASCADE_FLARE_DAMAGE, POWERUP_CASCADE_FLARE, w.player.id, false, 0, null);
  }
}

function collect(w: World, p: Pickup): void {
  const st = stateOf(w);
  const player = w.player;
  if (p.pickup_kind === 'xp_mote') {
    // AMD-45 — the tier no longer multiplies XP. It multiplied a supply that was itself a
    // function of enemy density, and the meter is fed by that same density, so §9.5 was the
    // gain stage of a loop whose input was the difficulty curve: more pressure bought more
    // motes, more motes bought a higher tier, a higher tier bought more XP *and* more damage,
    // and more damage bought more motes. Measured, it was a latch as much as a multiplier —
    // under roughly one mote every 3 s the meter bleeds faster than it fills (§9.5.1 decay)
    // and the tier pins at 1, which is why the same pilot landed at level 5 on the shipped
    // curve and level 32 with the budget doubled. §9.5 keeps its damage and §9.12 ichor
    // multipliers, where the feedback runs through the player's play and not the director's.
    const gained = Math.floor(p.value * player.stats.xp_gain_mult + 0.5);
    player.xp += gained;
    w.counters.motes_collected += p.value;
    st.moteCount--;
    w.bus.emit('pickup', { entity_id: p.id, kind: 'xp_mote', value: gained, pos: { x: p.pos.x, y: p.pos.y } });
    addSurgeMeter(w, p.value);
  } else if (p.pickup_kind === 'health') {
    const before = player.hp;
    player.hp += HEAL_AMOUNT;
    if (player.hp > player.max_hp) player.hp = player.max_hp;
    st.healthCount--;
    w.bus.emit('player_heal', { amount: player.hp - before, hp_after: player.hp });
    w.bus.emit('pickup', { entity_id: p.id, kind: 'health', value: player.hp - before, pos: { x: p.pos.x, y: p.pos.y } });
  } else {
    st.powerupCount--;
    w.bus.emit('pickup', { entity_id: p.id, kind: 'powerup', value: 1, pos: { x: p.pos.x, y: p.pos.y } });
    activatePowerup(w, p.powerup_id, p.pos.x, p.pos.y);
  }
  w.pickups.release(p);
}

function despawnSecondsFor(kind: Pickup['pickup_kind']): number {
  if (kind === 'xp_mote') return MOTE_DESPAWN_S;
  if (kind === 'health') return HEALTH_DESPAWN_S;
  return POWERUP_DESPAWN_S;
}

/**
 * Step 12. Magnet, collection, despawn, denomination merge, Lumensurge decay.
 * Zero allocation: no closures, no literals, ascending pool order.
 */
export function stepPickups(w: World): void {
  const st = stateOf(w);
  const player = w.player;
  const radius = pickupRadius(w);
  const radiusSq = radius * radius;
  const trawl = st.until[0] > w.tick;

  let motes = 0;
  let healths = 0;
  let powers = 0;

  const list = w.pickups.liveIndices();
  for (let i = 0; i < list.length; i++) {
    const p = w.pickups.slots[list[i]];
    if (!p.alive) continue;

    const ageTicks = w.tick - p.spawn_tick;
    if (ageTicks >= despawnSecondsFor(p.pickup_kind) * 60) {
      w.pickups.release(p);
      continue;
    }

    p.bob_phase += STEP;
    if (p.bob_phase > 1000) p.bob_phase -= 1000;

    // AMD-24 — magnet and collection pull along the short way round the torus.
    const dx = wrapDeltaX(player.pos.x - p.pos.x);
    const dy = wrapDeltaY(player.pos.y - p.pos.y);
    const distSq = dx * dx + dy * dy;

    /*
     * A full-health player walks over a vital-sac and leaves it there.
     *
     * §9.7 says "Overheal wasted", and the shipped reading of that was that the pickup is
     * spent regardless: at full HP `collect` clamped the heal to zero and released the
     * entity, so touching one at 100/100 destroyed it for measurably nothing. The other
     * reading — the excess HP is wasted, not the sac — is the one a player expects, and it
     * is the one that makes §13.1.9's "what the run offered" line mean something: a sac left
     * on the field is a resource, and burning it by accident while topped up is not a
     * decision anyone made.
     *
     * Only health is gated. Motes and power-ups have no full state to be at, and the sac
     * keeps its own §10.9 despawn timer, so this hands out no permanent stockpile — it only
     * stops an involuntary waste.
     */
    const wouldWaste = p.pickup_kind === 'health' && player.hp >= player.max_hp;
    if (distSq <= COLLECT_RADIUS * COLLECT_RADIUS && !wouldWaste) {
      collect(w, p);
      continue;
    }

    const trawled = trawl && p.pickup_kind === 'xp_mote';
    if (distSq <= radiusSq || trawled) {
      const dist = Math.sqrt(distSq);
      const inv = dist > 1e-6 ? 1 / dist : 0;
      const nx = dx * inv;
      const ny = dy * inv;
      p.magnet = true;
      if (trawled && distSq > radiusSq) {
        // §9.6 — Lumen Trawl pulls at a flat 24 u/s regardless of distance.
        p.vel.x = nx * TRAWL_PULL_SPEED;
        p.vel.y = ny * TRAWL_PULL_SPEED;
      } else {
        p.vel.x += nx * MAGNET_ACCEL * STEP;
        p.vel.y += ny * MAGNET_ACCEL * STEP;
        const sp2 = p.vel.x * p.vel.x + p.vel.y * p.vel.y;
        if (sp2 > MAGNET_MAX_SPEED * MAGNET_MAX_SPEED) {
          const k = MAGNET_MAX_SPEED / Math.sqrt(sp2);
          p.vel.x *= k;
          p.vel.y *= k;
        }
      }
      p.pos.x += p.vel.x * STEP;
      p.pos.y += p.vel.y * STEP;
      wrapPos(p.pos);
    } else if (p.magnet) {
      p.magnet = false;
      p.vel.x = 0;
      p.vel.y = 0;
    }

    if (p.pickup_kind === 'xp_mote') motes++;
    else if (p.pickup_kind === 'health') healths++;
    else powers++;
  }

  st.moteCount = motes;
  st.healthCount = healths;
  st.powerupCount = powers;

  // §9.8.2 — hold the mote population at the cap so the 512-slot pool never exhausts.
  let guard = 0;
  while (st.moteCount > MOTE_ALIVE_CAP && guard++ < 64) {
    if (!mergeOldestMotePair(w)) break;
  }

  // §9.6 — Surge Anchor freezes the meter for the duration of its window.
  holdSurgeAnchor(w);
}
